import { getValues } from "../../designer-context";
import { addDesignerItemToFolder, createDesignerFolder } from "../../designer-folder-actions";
import { saveDesignerScreen } from "../../designer-project-actions";
import { deleteDesignerScreen, duplicateDesignerScreen, openDesignerScreen } from "../../designer-screen-actions";
import { buildDesignerTextTree } from "../../designer-screen-query-actions";
import { isManagedTaskScreenId } from "../../managed-task-screen";
import { canvasCarriesWizardStamp } from "../../managed-task-screen-ownership";
import { getSdInstance } from "../helpers";
import { resolveManagedGuardContext } from "../managed-guard-context";
import type { ToolHandlerGroup } from "../types";

/**
 * Builds a full component tree string for a screen after navigation.
 * Uses the same text-tree format as get_tree (maxDepth=10) so the LLM
 * has all kuikaIds without needing a separate get_tree call.
 */
function buildNavigateComponentTree(sd: any): string | undefined {
  const screenDesign = sd.screenProvider?.getScreenDesign?.();
  if (!screenDesign?.displayItems) return undefined;
  return buildDesignerTextTree(screenDesign.displayItems, screenDesign.name, 10);
}

const NAVIGATE_TREE_HINT: string =
  "Component tree omitted (includeTree=false). Call ui_screen_query.get_tree for the tree and its kuikaIds.";

/** What `navigate` reports about the screen it landed on: the full tree, or a compact summary. */
interface INavigateScreenView {
  componentTree?: string;
  /** Compact mode only — direct children of the screen's root sections (Header / Body / Footer). */
  topLevelComponentCount?: number;
  /** Compact mode only — where the omitted tree can be read. */
  treeHint?: string;
}

/** `includeTree` defaults to true; only an explicit false (boolean or its string form) opts out. */
function shouldIncludeNavigateTree(includeTree: unknown): boolean {
  return includeTree !== false && includeTree !== "false";
}

/**
 * Never throws — the tree is a convenience on top of a navigation that already
 * succeeded, so a failure here must not turn the result into an error.
 */
function buildNavigateScreenView(sd: any, includeTree: boolean): INavigateScreenView {
  try {
    if (includeTree) {
      const componentTree: string | undefined = buildNavigateComponentTree(sd);
      return componentTree ? { componentTree } : {};
    }
    const displayItems: unknown = sd.screenProvider?.getScreenDesign?.()?.displayItems;
    if (!Array.isArray(displayItems)) return { treeHint: NAVIGATE_TREE_HINT };
    let topLevelComponentCount: number = 0;
    for (const root of displayItems) {
      topLevelComponentCount += Array.isArray(root?.children) ? root.children.length : 0;
    }
    return { topLevelComponentCount, treeHint: NAVIGATE_TREE_HINT };
  } catch {
    return {};
  }
}

const SCREEN_TYPE_LABELS: Record<number, string> = {
  1: "Default",
  2: "Report",
  3: "Master",
  4: "WebScreen",
  5: "Component"
};

function getScreenRoles(screenId: string, project: any): string[] {
  if (!screenId || !project) return [];
  const roles: string[] = [];
  if (project.startingScreenId === screenId) roles.push("Starting Screen");
  if (project.signinScreenId === screenId) roles.push("Signin Screen");
  if (project.leftMenuScreenId === screenId) roles.push("Left Menu Screen");
  if (project.resetPasswordScreenId === screenId) roles.push("Reset Password Screen");
  if (project.splashScreenId === screenId) roles.push("Splash Screen");
  if (project.newPasswordScreenId === screenId) roles.push("New Password Screen");
  if (project.verificationCodeScreenId === screenId) roles.push("Verification Code Screen");
  if (project.offlineScreenId === screenId) roles.push("Offline Screen");
  if (project.documentUploadLinkScreenId === screenId) roles.push("Document Upload Link Screen");
  if (isManagedTaskScreenId(project, screenId)) {
    roles.push("Process Step Screen");
  }
  return roles;
}

export const screenTools: ToolHandlerGroup = {
  sync: {
    ui_common_list_screens: () => {
      const sd = getSdInstance();
      const screenList = sd.screenProvider?.state?.screenList;
      if (!screenList) return { error: "Screen list not available" };

      const screens = screenList.map((screen: any) => ({
        screenId: screen.id,
        name: screen.name,
        screenType: SCREEN_TYPE_LABELS[screen.publicMetaData?.screenType] || "Unknown"
      }));

      const project = sd.projectProvider?.getProject?.() || sd.projectProvider?.state?.project;
      const folders = project?.screenFolders || [];
      const folderSummary = folders.map((folder: any) => ({
        id: folder.id,
        name: folder.name,
        screenIds: folder.screenIds || [],
        children: (folder.children || []).map((child: any) => ({
          id: child.id,
          name: child.name,
          screenIds: child.screenIds || []
        }))
      }));

      // Only include role screen IDs that are actually set (non-null) to reduce token cost
      const roleScreenIds: Record<string, string> = {};
      if (project?.startingScreenId) roleScreenIds.startingScreenId = project.startingScreenId;
      if (project?.signinScreenId) roleScreenIds.signinScreenId = project.signinScreenId;
      if (project?.leftMenuScreenId) roleScreenIds.leftMenuScreenId = project.leftMenuScreenId;

      return {
        count: screens.length,
        screens,
        ...(folderSummary.length > 0 ? { folders: folderSummary } : {}),
        ...roleScreenIds
      };
    },

    ui_common_get_current_screen: () => {
      const sd = getSdInstance();
      const screenDesign = sd.screenProvider.getScreenDesign();
      if (!screenDesign) return { error: "No screen loaded" };

      const project = sd.projectProvider?.getProject?.() || sd.projectProvider?.state?.project;
      const screenId = screenDesign.id || sd.screenProvider?.state?.sdId;

      // Screen type
      const screenList = sd.screenProvider?.state?.screenList;
      let screenType = "Unknown";
      if (screenList && screenId) {
        const currentScreen = screenList.find((s: any) => s.id === screenId);
        const typeNum = currentScreen?.publicMetaData?.screenType;
        if (typeNum != null) {
          screenType = SCREEN_TYPE_LABELS[typeNum] || `Unknown(${typeNum})`;
        }
      }

      const hasProcessStep = isManagedTaskScreenId(project, screenId);

      return {
        screenId,
        name: screenDesign.name || sd.screenProvider?.state?.sdName,
        screenType,
        isStartingScreen: project?.startingScreenId === screenId,
        isSigninScreen: project?.signinScreenId === screenId,
        isLeftMenuScreen: project?.leftMenuScreenId === screenId,
        isSplashScreen: project?.splashScreenId === screenId,
        isResetPasswordScreen: project?.resetPasswordScreenId === screenId,
        isNewPasswordScreen: project?.newPasswordScreenId === screenId,
        isVerificationCodeScreen: project?.verificationCodeScreenId === screenId,
        isOfflineScreen: project?.offlineScreenId === screenId,
        isDocumentUploadLinkScreen: project?.documentUploadLinkScreenId === screenId,
        isProcessStepScreen: !!hasProcessStep
      };
    },

    ui_project_create_screen_folder: (input) =>
      createDesignerFolder(getSdInstance(), { folderType: "screen", folderName: input.folderName }),

    ui_project_move_screen_to_folder: (input) =>
      addDesignerItemToFolder(getSdInstance(), {
        folderType: "screen",
        folderId: input.folderId,
        itemId: input.screenId
      })
  },

  async: {
    ui_project_navigate_to_screen: async (input) => {
      const { screenId } = input;
      if (!screenId) return { error: "screenId is required" };
      const includeTree: boolean = shouldIncludeNavigateTree(input.includeTree);

      const sd = getSdInstance();
      const currentSd = getValues(sd);
      const currentScreenDesign = sd.screenProvider?.getScreenDesign?.();
      if (currentSd?.sdId === screenId && currentScreenDesign?.displayItems) {
        // Preserve the saveScreen invariant from openDesignerScreen — the
        // 23s cost that motivated this fast-path came from loadScreenAndWait,
        // not the save, so we keep the save and skip only the reload.
        // No reload happens on this path, so a rejected save costs nothing here —
        // the in-memory tree survives. Report it so the agent knows the screen is
        // not durable yet instead of reading silence as success.
        let persisted: boolean = true;
        try {
          persisted = (await sd.screenProvider?.saveScreen?.(true)) !== false;
        } catch {
          persisted = false;
        }
        const screenName = currentScreenDesign.name ?? "";
        return {
          success: true,
          message: `Already on screen "${screenName}"`,
          screenId,
          screenName,
          alreadyActive: true,
          ...(persisted
            ? {}
            : {
                warning:
                  "The screen could not be persisted (usually a live collaboration session holds it). " +
                  "Its components exist on the canvas but are NOT saved — a reload will lose them."
              }),
          ...buildNavigateScreenView(sd, includeTree)
        };
      }

      // Programmatic navigation: a rejected save must not be followed by a
      // reload — that is what silently wiped agent-built screens.
      const result = await openDesignerScreen(sd, screenId, true);
      if ("error" in result) return result;

      // Default: build the full component tree so the LLM has correct kuikaIds
      // without needing a separate get_tree call (prevents ID hallucination).
      // `includeTree: false` swaps it for a compact summary.
      const screenView: INavigateScreenView = result.ready ? buildNavigateScreenView(getSdInstance(), includeTree) : {};

      return {
        success: true,
        message: `Navigated to screen "${result.screenName}"`,
        screenId: result.screenId,
        screenName: result.screenName,
        // Compact mode states it explicitly; the default shape is unchanged.
        ...(includeTree ? {} : { alreadyActive: false }),
        ...screenView,
        ...(result.ready
          ? {}
          : {
              warning:
                "Navigation succeeded but the designer may not have fully loaded the screen yet. Subsequent tool calls might see stale state."
            })
      };
    },

    ui_project_delete_screen: async (input) => {
      const { screenId } = input;
      if (!screenId) return { error: "screenId is required" };

      const sd = getSdInstance();
      const screenList = sd.screenProvider?.state?.screenList;
      if (!screenList || screenList.length <= 1) {
        return { error: "Cannot delete the last screen in the project" };
      }

      // Guard: prevent deleting screens with project roles
      const project = sd.projectProvider?.getProject?.() || sd.projectProvider?.state?.project;
      if (project) {
        const roles = getScreenRoles(screenId, project);
        if (roles.length > 0) {
          return {
            error: `Cannot delete this screen because it is assigned as: ${roles.join(", ")}. Remove the role assignment in project settings before deleting.`
          };
        }
      }

      // KOA-18136 §132 — THE SECOND WITNESS REACHES DELETION TOO.
      //
      // The role check above recognises a process task screen through
      // `project.processStepScreenIds`, and that registry is measurably not
      // dependable: it answers during a build and is gone when a later pass asks
      // (live `3e95f572` — a turn wrote all over two existing task screens because
      // the registry did not name them at the time). `canvasCarriesWizardStamp` was
      // added as a registry-independent witness for exactly that, and it was wired
      // only to the guard that protects a task screen's CONTENTS. Deletion — the one
      // loss that cannot be undone by re-running anything — still had a single
      // source.
      //
      // The stamp is only readable for the canvas that is currently loaded, so this
      // covers "delete the screen I am looking at" and not an arbitrary id. That is a
      // strict addition: a screen this cannot see is refused exactly as before,
      // never more permissively.
      // Read through the ONE resolver that knows where these live: `project` / `sdId`
      // sit on the flat state behind `getValues`, the component dictionary hangs off
      // `screenProvider.state.screenDesignDictionary`, and reading them straight off
      // the instance yields `undefined` and silently disables the check (live
      // 2026-07-27).
      const guardCtx = resolveManagedGuardContext();
      if (guardCtx !== null && guardCtx.screenId === screenId) {
        if (canvasCarriesWizardStamp(guardCtx.dict)) {
          return {
            error:
              "Cannot delete this screen: it is a platform-managed workflow task screen (its components carry the " +
              "workflow wizard's stamp). The screen belongs to a process step — remove the step from the process " +
              "instead, with ui_workflow_dsl."
          };
        }
      }

      const result = await deleteDesignerScreen(sd, screenId);
      if ("error" in result) return result;

      return {
        success: true,
        message: `Deleted screen "${result.screenName}"`,
        ...(result.nextScreenId ? { nextScreenId: result.nextScreenId } : {}),
        ...(result.ready === false
          ? {
              warning:
                "Screen deleted but the replacement screen may not have fully loaded yet. Subsequent tool calls might see stale state."
            }
          : {})
      };
    },

    ui_project_rename_screen: async (input) => {
      if (!input.newName) return { error: "newName is required" };
      return saveDesignerScreen(getSdInstance(), { withValidation: true, newName: input.newName });
    },

    ui_project_duplicate_screen: async (input) => {
      const { screenId } = input;
      if (!screenId) return { error: "screenId is required" };

      const result = await duplicateDesignerScreen(getSdInstance(), screenId);
      if ("error" in result) return result;

      return {
        success: true,
        message: `Duplicated screen "${result.sourceScreenName}"`,
        screenId: result.screenId,
        ...(result.ready
          ? {}
          : {
              warning:
                "Screen duplicated but the designer may not have fully loaded it yet. Subsequent tool calls might see stale state."
            })
      };
    }
  }
};
